import { FC, useState } from 'react';
import { Rnd } from 'react-rnd';
import { Document, Page } from 'react-pdf';
import { XMarkIcon, ArrowsPointingOutIcon, ArrowsPointingInIcon, PlusIcon, MinusIcon } from '@heroicons/react/24/outline';
import { pdfOptions } from '@/utils/pdfConfig';
import MarkdownContent from '@/components/ui/MarkdownContent/MarkdownContent';

interface ReadingInsertModalProps {
  isOpen: boolean;
  onClose: () => void;
  pdfUrl?: string;
  pageNumbers?: number[];
  textContent?: string;
  title?: string;
  defaultPosition?: 'left' | 'right';
}

const ZOOM_LEVELS = [0.5, 0.75, 1, 1.25, 1.5, 2];
const DEFAULT_ZOOM_INDEX = 2; // 100%

const getInitialPosition = (side: 'left' | 'right') => {
  if (side === 'left') {
    return { x: 50, y: 100 };
  }
  return { x: window.innerWidth - 650, y: 100 };
};

const ReadingInsertModal: FC<ReadingInsertModalProps> = ({
  isOpen,
  onClose,
  pdfUrl,
  pageNumbers,
  textContent,
  title = 'Reading Text Insert',
  defaultPosition = 'right',
}) => {
  const [isMaximized, setIsMaximized] = useState(false);
  const [size, setSize] = useState({ width: 600, height: 500 });
  const [position, setPosition] = useState(getInitialPosition(defaultPosition));
  const [zoomIndex, setZoomIndex] = useState(DEFAULT_ZOOM_INDEX);

  const zoom = ZOOM_LEVELS[zoomIndex];

  if (!isOpen) return null;

  const handleZoomIn = () => {
    if (zoomIndex < ZOOM_LEVELS.length - 1) {
      setZoomIndex(zoomIndex + 1);
    }
  };

  const handleZoomOut = () => {
    if (zoomIndex > 0) {
      setZoomIndex(zoomIndex - 1);
    }
  };

  const handleMaximize = () => {
    if (isMaximized) {
      setSize({ width: 600, height: 500 });
      setPosition({ x: window.innerWidth - 650, y: 100 });
    } else {
      setSize({ width: window.innerWidth - 100, height: window.innerHeight - 100 });
      setPosition({ x: 50, y: 50 });
    }
    setIsMaximized(!isMaximized);
  };

  return (
    <Rnd
      size={size}
      position={position}
      onDragStop={(_e, d) => setPosition({ x: d.x, y: d.y })}
      onResizeStop={(_e, _direction, ref, _delta, pos) => {
        setSize({ width: parseInt(ref.style.width), height: parseInt(ref.style.height) });
        setPosition(pos);
      }}
      minWidth={350}
      minHeight={300}
      bounds="window"
      dragHandleClassName="drag-handle"
      className="z-[9999]"
      style={{ position: 'fixed' }}
    >
      <div className="bg-white rounded-lg shadow-2xl border border-gray-200 flex flex-col h-full">
        {/* Header - Drag Handle */}
        <div className="drag-handle flex items-center justify-between border-b px-4 py-3 cursor-move bg-gray-50 rounded-t-lg">
          <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
          <div className="flex items-center gap-2">
            {/* Zoom Controls */}
            <div className="flex items-center gap-1 bg-gray-200 rounded px-1 py-0.5">
              <button
                onClick={handleZoomOut}
                disabled={zoomIndex === 0}
                className="p-1 hover:bg-gray-300 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                title="Zoom out"
              >
                <MinusIcon className="w-4 h-4 text-gray-600" />
              </button>
              <span className="text-xs font-medium text-gray-700 min-w-[40px] text-center">
                {Math.round(zoom * 100)}%
              </span>
              <button
                onClick={handleZoomIn}
                disabled={zoomIndex === ZOOM_LEVELS.length - 1}
                className="p-1 hover:bg-gray-300 rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                title="Zoom in"
              >
                <PlusIcon className="w-4 h-4 text-gray-600" />
              </button>
            </div>
            {/* Window Controls */}
            <button
              onClick={handleMaximize}
              className="p-1.5 hover:bg-gray-200 rounded transition-colors"
              title={isMaximized ? 'Restore' : 'Maximize'}
            >
              {isMaximized ? (
                <ArrowsPointingInIcon className="w-5 h-5 text-gray-600" />
              ) : (
                <ArrowsPointingOutIcon className="w-5 h-5 text-gray-600" />
              )}
            </button>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-red-100 rounded transition-colors"
              title="Close"
            >
              <XMarkIcon className="w-5 h-5 text-gray-600 hover:text-red-600" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          {textContent ? (
            <div style={{ fontSize: `${zoom}rem` }}>
              <MarkdownContent content={textContent} className="text-gray-800" />
            </div>
          ) : pdfUrl && pageNumbers ? (
            <Document file={pdfUrl} options={pdfOptions}>
              {pageNumbers.map((pageNum) => (
                <div key={pageNum} className="mb-4 inline-block">
                  <p className="text-xs text-gray-500 mb-2">Page {pageNum}</p>
                  <Page
                    pageNumber={pageNum}
                    width={Math.min(size.width - 50, 700) * zoom}
                    renderTextLayer={true}
                    renderAnnotationLayer={false}
                    className="shadow-md"
                  />
                </div>
              ))}
            </Document>
          ) : null}
        </div>

        {/* Resize indicator */}
        <div className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize" />
      </div>
    </Rnd>
  );
};

export default ReadingInsertModal;
